export const primitives = {
  color: {
    /**
     * "Actinic Glow" (ReefGuide's own look): the colours corals fluoresce in
     * under actinic light, on the blue-black of a tank at night. The bright
     * hues are for the dark theme; the deep shades carry them on light
     * backgrounds (text ≥ 4.5:1, see tokens/__tests__/contrast.test.ts).
     */
    actinic: {
      green: "#4dffb0",
      greenSoft: "#9dffd3",
      greenDeep: "#007a52",
      greenDeeper: "#00603f",
      magenta: "#ff5cd6",
      magentaDeep: "#b0168c",
      gold: "#ffc94d",
      goldDeep: "#8a5a00",
      sky: "#6fc3ff",
      skyDeep: "#1d5fc0",
      coral: "#ff6b7a",
      coralDeep: "#c01f36",
      violet: "#c49bff",
      violetDeep: "#7034dc",
    },
    night: {
      950: "#02060f",
      900: "#060b18",
      850: "#0b1426",
      800: "#0f1d36",
      750: "#132341",
      700: "#182a4c",
      650: "#1b2742",
      600: "#142038",
      500: "#62779e",
      400: "#8e9fbd",
      300: "#b9c7de",
      200: "#eaf2ff",
    },
    tide: {
      0: "#ffffff",
      50: "#f2f6fb",
      100: "#e9f0f8",
      150: "#e6eef7",
      200: "#d5dfeb",
      250: "#e6edf5",
      400: "#9aa7ba",
      500: "#6b7c95",
      600: "#55667f",
      700: "#3a4a66",
      900: "#0a1730",
    },
    reef: {
      pink: "#ff4d8d",
      orange: "#ff7d4d",
      purple: "#9c4dff",
      blue: "#2979ff",
      cyan: "#00c8ff",
      teal: "#20d6b7",
    },
    neutral: {
      0: "#ffffff",
      25: "#f8fbff",
      50: "#f5f8fb",
      100: "#eef3f8",
      200: "#dde6f0",
      300: "#c7d3e0",
      400: "#a0aec0",
      500: "#798b9f",
      550: "#5d6f82",
      600: "#4a6078",
      650: "#5d7998",
      700: "#24364d",
      800: "#142235",
      850: "#0d1726",
      900: "#07111f",
      950: "#030914",
      /** Pure black: letterbox behind photos (background.media). */
      1000: "#000000",
    },
    blue: {
      50: "#eef6ff",
      100: "#d9ecff",
      400: "#7cbfff",
      500: "#4ea8ff",
      600: "#2979ff",
      700: "#155bd6",
      800: "#1351bf",
    },
    green: {
      50: "#eafaf2",
      500: "#24d17e",
      600: "#1dbb72",
      700: "#137d4c",
    },
    amber: {
      50: "#fff7e6",
      500: "#ffb020",
      600: "#f5a623",
      700: "#976107",
    },
    red: {
      50: "#fff0f2",
      500: "#ff5d5d",
      600: "#e84c63",
      700: "#d61c37",
    },
    /**
     * Deep shades of the reef hues for data series on light backgrounds (the
     * bright reef.* hues fall below 3:1 on white — WCAG 1.4.11), #1653.
     */
    teal: {
      700: "#0f8a75",
    },
    orange: {
      700: "#c2410c",
    },
    cyan: {
      700: "#0e7490",
    },
    violet: {
      50: "#f4efff",
      400: "#b376ff",
      500: "#9c4dff",
      600: "#8b5cf6",
      700: "#7c46f5",
    },
  },
  spacing: {
    0: "0",
    "0-5": "0.125rem",
    1: "0.25rem",
    "1-5": "0.375rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    8: "2rem",
    10: "2.5rem",
    12: "3rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "2rem",
    "4xl": "2.5rem",
  },
  fontWeight: {
    regular: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
    extrabold: "800",
  },
  lineHeight: {
    none: "1",
    tight: "1.2",
    snug: "1.35",
    normal: "1.5",
    relaxed: "1.75",
  },
  radius: {
    sm: "0.5rem",
    md: "0.875rem",
    lg: "1.25rem",
    xl: "1.5rem",
    hero: "2rem",
    full: "999px",
  },
  shadow: {
    none: "none",
    sm: "0 2px 8px rgba(16, 35, 58, 0.05)",
    md: "0 8px 24px rgba(16, 35, 58, 0.08)",
    lg: "0 16px 48px rgba(16, 35, 58, 0.12)",
    darkSm: "0 2px 8px rgba(0, 0, 0, 0.22)",
    darkMd: "0 12px 32px rgba(0, 0, 0, 0.28)",
    darkLg: "0 24px 64px rgba(0, 0, 0, 0.38)",
    focusLight: "0 0 0 3px rgba(41, 121, 255, 0.35)",
    focusDark: "0 0 0 3px rgba(77, 255, 176, 0.45)",
    /** Signature glow of primary actions and coral markers (Actinic Glow). */
    glowLight: "0 6px 20px rgba(0, 122, 82, 0.22)",
    glowDark: "0 0 24px rgba(77, 255, 176, 0.35)",
  },
  zIndex: {
    dropdown: "1000",
    sticky: "1050",
    modal: "1100",
    toast: "1200",
  },
  breakpoint: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  fontFamily: {
    sans: "Figtree, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    /** Headings and big numbers: the brand's voice. */
    display: "'Bricolage Grotesque', Figtree, ui-sans-serif, system-ui, sans-serif",
    /** Measured values, units, technical labels. */
    mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
} as const;

export type Primitives = typeof primitives;
